import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Terminal, Sparkles, Check, ArrowRight, Boxes, Blocks, FolderTree,
} from 'lucide-react';

// Homepage "what do you want to build" picker. One source — a video, article,
// PDF, or your own expertise — can become a *skill package* (instructions an
// agent reads) or a *CLI + MCP* (tools an agent, or a human, runs). The two
// cards on the left drive the live preview panel on the right.
const PATHS = [
  {
    id: 'skill',
    Icon: FileText,
    kicker: 'Path A',
    title: 'A skill package',
    tagline: 'Instructions your agent reads.',
    blurb:
      'A production-ready bundle that drops straight into .claude/skills/ — no editing, no glue code.',
    bullets: [
      'SKILL.md + README, written for the model',
      'Reference stubs for the details that matter',
      'A runnable test so you know it works',
      'Ranked themes — keep the best, drop the rest',
    ],
    preview: {
      label: 'unzip → .claude/skills/',
      lines: [
        { t: 'building-branded-echarts/', c: 'dir' },
        { t: '├── SKILL.md', c: 'file' },
        { t: '├── README.md', c: 'file' },
        { t: '├── references/', c: 'dir' },
        { t: '│   └── theming.md', c: 'file' },
        { t: '└── tests/test_echarts.py', c: 'file' },
      ],
    },
    cta: { to: '/dashboard/generate', label: 'Build a skill package' },
  },
  {
    id: 'cli',
    Icon: Terminal,
    kicker: 'Path B',
    title: 'A CLI + MCP',
    tagline: 'Tools your agent runs.',
    blurb:
      'The same skill, bundled with a zero-dependency CLI and an MCP server. Install once, invoke as a slash command.',
    bullets: [
      'One-command install — skill, CLI & MCP together',
      'Every action is a CLI command and an MCP tool',
      'Drive it with /slash commands inside Claude Code',
      'Runs locally — no round-trips, no API keys',
    ],
    preview: {
      label: 'one command to install + run',
      lines: [
        { t: '$ npx -y @loreto/skill-echarts install', c: 'cmd' },
        { t: '✓ skill + CLI + MCP installed', c: 'ok' },
        { t: '', c: 'file' },
        { t: '$ /echarts render --brand loreto', c: 'cmd' },
        { t: '→ chart.svg  (rendered locally, 48ms)', c: 'out' },
      ],
    },
    cta: { to: '/dashboard/generate?artifact=cli', label: 'Build a CLI' },
  },
];

const lineColor = {
  dir: 'text-[var(--accent)]',
  file: 'text-[var(--text-3)]',
  cmd: 'text-[var(--text)]',
  ok: 'text-[var(--syn-string)]',
  out: 'text-[var(--text-2)]',
};

export default function BuildPicker() {
  const [active, setActive] = useState('skill');
  const path = PATHS.find((p) => p.id === active);

  return (
    <section className="relative border-t border-[var(--border)] bg-[var(--bg)]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-32">
        <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
          <span className="divider-num">(03)</span>
          <span className="eyebrow">Start building</span>
          <span className="flex-1 thin-line ml-2" />
        </div>

        <h2 className="serif text-[2.3rem] sm:text-[2.8rem] md:text-[3.3rem] lg:text-[3.9rem] xl:text-[4.2rem] leading-[1.04] tracking-[-0.02em] lg:whitespace-nowrap mb-6">
          One source. <span className="italic text-[var(--text-2)]">Two ways to ship.</span>
        </h2>
        <p className="text-[var(--text-2)] text-[1.02rem] leading-[1.7] max-w-[58ch] mb-14">
          Point Loreto at a YouTube video, an article, a PDF, or your own expertise and get one
          piece of the harness back. Instructions your agent reads, or tools it actually runs —
          the same source, two shapes. Pick a path.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
          {/* Left — the two selectable paths */}
          <div className="bg-[var(--bg)] flex flex-col">
            {PATHS.map((p) => {
              const isActive = p.id === active;
              const { Icon } = p;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActive(p.id)}
                  aria-pressed={isActive}
                  className={`group text-left p-8 md:p-10 flex-1 border-b border-[var(--border)] last:border-b-0 transition-colors relative ${
                    isActive ? 'bg-[var(--bg-2)]' : 'hover:bg-[var(--bg-2)]/60'
                  }`}
                >
                  <span
                    className={`absolute left-0 top-0 bottom-0 w-[2px] transition-colors ${
                      isActive ? 'bg-[var(--accent)]' : 'bg-transparent'
                    }`}
                  />
                  <div className="flex items-center justify-between mb-6">
                    <span className="mono text-[0.64rem] tracking-[0.18em] uppercase text-[var(--text-4)]">
                      {p.kicker}
                    </span>
                    <Icon
                      size={24}
                      className={`transition-colors ${
                        isActive ? 'text-[var(--accent)]' : 'text-[var(--text-4)] group-hover:text-[var(--text-3)]'
                      }`}
                    />
                  </div>
                  <h3 className="serif text-[2.1rem] leading-tight mb-1">{p.title}</h3>
                  <p
                    className={`text-[0.95rem] mb-3 transition-colors ${
                      isActive ? 'text-[var(--accent)]' : 'text-[var(--text-3)]'
                    }`}
                  >
                    {p.tagline}
                  </p>
                  <p className="text-[var(--text-2)] text-[0.92rem] leading-[1.6]">{p.blurb}</p>
                </button>
              );
            })}
          </div>

          {/* Right — live preview of the selected path */}
          <div className="bg-[var(--bg-2)] p-8 md:p-10 flex flex-col">
            <div className="flex items-center gap-2 mb-7 text-[var(--text-3)]">
              <Boxes size={15} className="text-[var(--accent)]" />
              <span className="mono text-[0.66rem] tracking-[0.16em] uppercase">What you get</span>
            </div>

            <ul key={active} className="space-y-3 mb-8 fade-up">
              {path.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-[var(--text-2)] text-[0.95rem] leading-[1.5]">
                  <Check size={16} className="text-[var(--accent)] mt-[3px] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            {/* Faux terminal / file-tree preview */}
            <div className="rounded-sm border border-[var(--border-2)] bg-[var(--bg)] overflow-hidden mb-8">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border)]">
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--border-2)]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--border-2)]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--border-2)]" />
                <span className="mono text-[0.62rem] tracking-[0.14em] uppercase text-[var(--text-4)] ml-2">
                  {path.preview.label}
                </span>
              </div>
              <pre key={active} className="mono text-[0.8rem] leading-[1.7] p-5 overflow-x-auto fade-up">
                {path.preview.lines.map((ln, i) => (
                  <div key={i} className={lineColor[ln.c]}>
                    {ln.t || ' '}
                  </div>
                ))}
              </pre>
            </div>

            <div className="mt-auto flex flex-wrap items-center gap-3">
              <Link to={path.cta.to} className="btn-primary !py-3 !px-6">
                <Sparkles size={16} /> {path.cta.label}
              </Link>
              <Link to="/dashboard/generate" className="btn-ghost !py-3 !px-6">
                <Blocks size={16} /> Build both <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center gap-x-8 gap-y-2 text-[var(--text-3)]">
          <span className="inline-flex items-center gap-2 mono text-[0.7rem] tracking-[0.12em] uppercase">
            <FolderTree size={13} className="text-[var(--accent)]" /> Ships ready to install
          </span>
          <span className="inline-flex items-center gap-2 mono text-[0.7rem] tracking-[0.12em] uppercase">
            <Terminal size={13} className="text-[var(--accent)]" /> Zero-dependency runtime
          </span>
          <span className="inline-flex items-center gap-2 mono text-[0.7rem] tracking-[0.12em] uppercase">
            <Sparkles size={13} className="text-[var(--accent)]" /> Free to start
          </span>
        </div>
      </div>
    </section>
  );
}
